import React, { useEffect, useMemo, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import DocumentosPersona from '../components/documentos/DocumentosPersona'
import HogarPersona from '../components/familia/HogarPersona'
import FamiliaYAyuda from '../components/familia/FamiliaYAyuda'
import { nombreSede } from '../components/comedores/comun'
import { hasSection } from '../config/permissions'
import { asistenciasApi, personasApi, familyApi, soporteApi, inundacionesApi } from '../services/api'
import { Loading, Modal } from '../components/common'
import FamilyMemberForm from '../components/assistance/FamilyMemberForm'
import { useAssistanceTypes } from '../hooks/useAssistanceTypes'
import { BARRIO_OPTIONS, normalizeBarrioLabel, SAN_ROQUE_BARRIOS } from '../config/barrios'
import toast from 'react-hot-toast'
import useAuthStore from '../stores/authStore'
import AssistanceTypeCombobox from '../components/forms/AssistanceTypeCombobox'
import { getFbMatchMeta } from '../utils/fbMatch'
import { confirmar, pedirTexto } from '../lib/confirmar'

// "“Herederos del Reino”." no dice qué es: "Comedor Herederos del Reino" sí.
const nombrePrograma = (p) => (p.tipo === 'comedor' ? `🍽️ Comedor ${nombreSede(p.nombre)}` : (p.nombre || p.tipo))

const Profile = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const [contexto, setContexto] = useState(null)
  const [familiares, setFamiliares] = useState([])
  const [relaciones, setRelaciones] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeTab, setActiveTab] = useState('resumen')
  const [showFamilyForm, setShowFamilyForm] = useState(false)
  const [editingMember, setEditingMember] = useState(null)
  const [isSavingFamily, setIsSavingFamily] = useState(false)
  const { loading: typesLoading, data: assistanceTypes, labelByValue } = useAssistanceTypes()

  // Modals state
  const [showIncompleteModal, setShowIncompleteModal] = useState(false)

  // Edit state
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [editForm, setEditForm] = useState({})
  const [updating, setUpdating] = useState(false)
  const [fbMatch, setFbMatch] = useState(null)
  const [evacuaciones, setEvacuaciones] = useState([])
  
  // Assistance Edit state
  const [editingAsistencia, setEditingAsistencia] = useState(null)
  const [editAsistenciaForm, setEditAsistenciaForm] = useState({ fecha: '', detalle: '', tipo: '' })
  const [savingAsistencia, setSavingAsistencia] = useState(false)

  const { user } = useAuthStore()
  const role = String(user?.rol || '').trim().toLowerCase()
  const isAdmin = role === 'admin' || role === 'administrator'
  const fbMeta = isAdmin && (fbMatch || data?.persona?.redes_sociales?.facebook)
    ? getFbMatchMeta(fbMatch, data?.persona)
    : null

  const effectiveFbHref = useMemo(() => {
    const raw = data?.persona?.redes_sociales?.facebook || fbMatch?.fb_perfil_override || fbMatch?.fb_perfil || null
    if (!raw) return null
    const s = String(raw).trim()
    if (!s) return null
    if (/^https?:\/\//i.test(s)) return s
    const clean = s.replace(/^@/, '').replace(/^(?:www\.)?(?:facebook\.com|fb\.com)\/?/i, '').split('/')[0].split('?')[0].trim()
    return clean ? `https://www.facebook.com/${clean}/` : null
  }, [data?.persona?.redes_sociales?.facebook, fbMatch?.fb_perfil_override, fbMatch?.fb_perfil])

  useEffect(() => {
    loadProfile()
  }, [id])

  // Después de cargar un tutor/a desde la ficha: solo se relee el contexto.
  const recargarContexto = async () => {
    try {
      const r = await personasApi.getContexto(id)
      setContexto(r.data?.data || null)
    } catch {
      toast.error('No se pudo actualizar la familia')
    }
  }

  const loadProfile = async () => {
    try {
      setLoading(true)
      const [response, contextoResponse] = await Promise.all([
        personasApi.getHistorial(id),
        personasApi.getContexto(id).catch(() => null)
      ])
      setData(response.data.data)
      setContexto(contextoResponse?.data?.data || null)
      setFamiliares(response.data.data.familiares || [])
      setRelaciones(response.data.data.relaciones || [])

      // Match FB por DNI (desde tabla importada en BD)
      try {
        const matchRes = await personasApi.getFbMatch(id)
        setFbMatch(matchRes?.data?.data ?? null)
      } catch (_) {
        setFbMatch(null)
      }

      // Fetch evacuaciones
      try {
        const evacRes = await inundacionesApi.getEvacuados({ persona_id: id })
        if (evacRes.data.success) {
          setEvacuaciones(evacRes.data.data)
        }
      } catch (err) {
        console.error('Error fetching evacuations:', err)
      }
    } catch (err) {
      console.error('Error loading profile:', err)
      const serverError = err.response?.data?.error || err.message
      setError(`Error al cargar el perfil: ${serverError}`)
    } finally {
      setLoading(false)
    }
  }

  const handleAddFamilyMember = async (formData) => {
    try {
      setIsSavingFamily(true)
      await familyApi.addMember({
        ...formData,
        persona_principal_id: id
      })
      toast.success('Familiar agregado correctamente')
      setShowFamilyForm(false)
      loadProfile()
    } catch (error) {
      console.error('Error adding family member:', error)
      toast.error(error.response?.data?.error || 'Error al agregar familiar')
    } finally {
      setIsSavingFamily(false)
    }
  }

  const handleUpdateFamilyMember = async (formData) => {
    try {
      setIsSavingFamily(true)
      await familyApi.updateMember(editingMember.id, formData)
      toast.success('Familiar actualizado correctamente')
      setShowFamilyForm(false)
      setEditingMember(null)
      loadProfile()
    } catch (error) {
      console.error('Error updating family member:', error)
      toast.error(error.response?.data?.error || 'Error al actualizar familiar')
    } finally {
      setIsSavingFamily(false)
    }
  }

  const handleDeleteFamilyMember = async (memberId) => {
    if (!(await confirmar({ titulo: '¿Eliminar este familiar?', mensaje: 'Se quita el vínculo con esta persona. Queda archivado en el historial.', accion: 'Eliminar' }))) return
    try {
      await familyApi.removeMember(memberId)
      toast.success('Familiar eliminado')
      loadProfile()
    } catch (error) {
      console.error('Error removing family member:', error)
      toast.error('Error al eliminar familiar')
    }
  }

  const openEditModal = () => {
    const p = data?.persona || {}
    const currentFb = p.redes_sociales?.facebook || fbMatch?.fb_perfil_override || fbMatch?.fb_perfil || ''
    setEditForm({
      nombre: p.nombre || '',
      apellido: p.apellido || '',
      dni: p.dni || '',
      fecha_nacimiento: p.fecha_nacimiento ? p.fecha_nacimiento.slice(0, 10) : '',
      sexo: p.sexo || p.genero || '',
      telefono: p.telefono || '',
      telefonos_adicionales: p.telefonos_adicionales || '',
      email: p.email || '',
      facebook: currentFb,
      instagram: p.redes_sociales?.instagram || '',
      barrio: p.barrio || '',
      direccion: p.direccion || '',
      es_personal_municipal: Boolean(p.es_personal_municipal),
      observaciones_personal: p.observaciones_personal || ''
    })
    setEditModalOpen(true)
  }

  const handleUpdateProfile = async (e) => {
    e.preventDefault()
    setUpdating(true)
    try {
      const payload = {
        ...editForm,
        redes_sociales: {
          facebook: editForm.facebook?.trim() || '',
          instagram: editForm.instagram?.trim() || ''
        }
      }
      await personasApi.update(id, payload)
      await loadProfile()
      setEditModalOpen(false)
      toast.success('Perfil actualizado correctamente')
    } catch (err) {
      console.error(err)
      toast.error('Error al actualizar: ' + (err.response?.data?.error || err.message))
    } finally {
      setUpdating(false)
    }
  }

  const handleReportIncorrectProfile = async () => {
    const reason = await pedirTexto({
      titulo: 'Reportar el perfil de Facebook sugerido',
      mensaje: 'Contá brevemente cuál es el error. Se crea un ticket en Soporte Técnico.',
      etiqueta: 'Motivo',
      placeholder: 'No corresponde a esta persona / DNI incorrecto…',
      accion: 'Reportar',
      peligro: false,
    });
    if (reason === null) return; // Cancelado

    try {
      const ticketText = `TICKET OPERATIVO AUTOMÁTICO:\n` +
        `El usuario ${user?.usuario || 'Operador'} reportó un error de perfil de Facebook sugerido en el ciudadano:\n` +
        `· Nombre: ${persona.apellido}, ${persona.nombre}\n` +
        `· DNI: ${persona.dni}\n` +
        `· ID Ficha: ${persona.id}\n\n` +
        `Motivo del reporte:\n` +
        `"${reason || 'No especificado por el operario'}"\n\n` +
        `Por favor, revise y corrija el enlace correspondiente.`;

      await soporteApi.create(ticketText);
      toast.success('Ticket creado en Soporte Técnico correctamente');
    } catch (err) {
      console.error(err);
      toast.error('Error al crear el ticket de soporte');
    }
  }

  const handleEditAsistenciaOpen = (ast) => {
    setEditingAsistencia(ast)
    // Extract date correctly, handling possible nulls
    let fechaDate = ''
    if (ast.fecha) {
      fechaDate = ast.fecha.slice(0, 10)
    }
    setEditAsistenciaForm({
      fecha: fechaDate,
      detalle: ast.detalle || '',
      tipo: ast.tipo || ''
    })
  }

  const handleEditAsistenciaSave = async () => {
    if (!editingAsistencia) return
    try {
      setSavingAsistencia(true)
      await asistenciasApi.update(editingAsistencia.id, {
        fecha: editAsistenciaForm.fecha || undefined,
        detalle: editAsistenciaForm.detalle || undefined,
        tipo: editAsistenciaForm.tipo || undefined
      })
      toast.success('Asistencia actualizada correctamente')
      setEditingAsistencia(null)
      loadProfile()
    } catch (error) {
      console.error(error)
      toast.error(error?.response?.data?.error || 'No se pudo actualizar la asistencia')
    } finally {
      setSavingAsistencia(false)
    }
  }

  const formatDate = (dateString, soloAnio) => {
    if (!dateString) return '-'
    if (soloAnio) {
      const m = String(dateString).match(/^(\d{4})/)
      return m ? `Aprox. ${m[1]} (solo año, base de datos)` : '-'
    }
    return new Date(dateString).toLocaleDateString('es-AR')
  }

  const formatSexo = (sexo) => {
    if (!sexo) return 'No especificado'
    const u = String(sexo).trim().toUpperCase()
    if (u === 'M') return 'Masculino'
    if (u === 'F') return 'Femenino'
    return sexo
  }

  const formatTipo = (tipo) => {
    if (!tipo) return '-'
    const normalized = String(tipo)
    return labelByValue.get(normalized) || normalized.replace(/_/g, ' ')
  }

  const getTipoIcon = (tipo) => {
    const t = String(tipo || '').toLowerCase()
    if (t.includes('medicamento')) {
      return (
        <svg className="w-6 h-6 animate-pulse-slow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
        </svg>
      )
    }
    if (t.includes('alimento') || t.includes('comestible') || t.includes('mercaderia')) {
      return (
        <svg className="w-6 h-6 animate-pulse-slow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      )
    }
    return (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
      </svg>
    )
  }

  const getEstadoBadge = (estado) => {
    const styles = {
      pendiente: 'bg-yellow-100 text-yellow-800',
      entregado: 'bg-green-100 text-green-800',
      aprobado: 'bg-blue-100 text-blue-800',
      rechazado: 'bg-red-100 text-red-800'
    }
    return styles[estado] || 'bg-gray-100 text-gray-800'
  }

  if (loading) return <Loading fullScreen />
  if (error) return <div className="p-8 text-red-600">{error}</div>
  if (!data) return <div className="p-8">No se encontró la persona</div>

  const { persona, asistencias } = data
  // Un chico (anotado en un comedor o menor de edad): su familia va arriba de todo.
  const nacimiento = persona.fecha_nacimiento ? new Date(persona.fecha_nacimiento) : null
  const edad = nacimiento && !Number.isNaN(nacimiento.getTime())
    ? Math.floor((Date.now() - nacimiento.getTime()) / (365.25 * 24 * 3600 * 1000))
    : null
  const esChico = (contexto?.familia?.inscripciones_comedor || []).some((i) => i.vigente) || (edad != null && edad < 18)

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center text-gray-600 hover:text-gray-900 transition-colors group"
      >
        <svg className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        <span className="font-medium">Volver</span>
      </button>

      {/* Profile Header (Premium Style) */}
      <div className="bg-gradient-to-r from-emerald-800 to-emerald-600 rounded-3xl p-8 text-white shadow-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 p-4 opacity-10 pointer-events-none">
          <svg className="w-48 h-48" fill="currentColor" viewBox="0 0 24 24">
            <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>

        <div className="flex flex-col md:flex-row items-center gap-6 relative z-10">
          <div className="w-24 h-24 rounded-full bg-white/20 backdrop-blur-md border-4 border-white/30 flex items-center justify-center text-3xl font-bold shadow-2xl">
            {String(persona.apellido || '')[0]?.toUpperCase()}{String(persona.nombre || '')[0]?.toUpperCase()}
          </div>
          <div className="flex-1 text-center md:text-left">
            <h1 className="text-3xl font-extrabold tracking-tight uppercase">
              {String(persona.apellido || '').toUpperCase()}, {String(persona.nombre || '').toUpperCase()}
            </h1>
            <div className="flex flex-wrap justify-center md:justify-start gap-3 mt-3">
              <span className="bg-white/10 px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 border border-white/20">
                DNI: {persona.dni}
              </span>
              <span className="bg-white/10 px-3 py-1 rounded-full text-xs font-bold border border-white/20 capitalize">
                {persona.barrio || 'Sin Barrio'}
              </span>
              <span className="bg-emerald-500/30 px-3 py-1 rounded-full text-xs font-bold border border-emerald-400/30">
                Cód: PER-{persona.id.toString().slice(-4)}
              </span>
              {data?.padron && (
                <span
                  className="bg-sky-300 text-sky-950 px-3 py-1 rounded-full text-xs font-black border border-sky-200 shadow-sm"
                  title={`Vota en ${data.padron.escuela || 'escuela sin dato'}${data.padron.mesa ? ` · Mesa ${data.padron.mesa}` : ''}${data.padron.orden ? ` · Orden ${data.padron.orden}` : ''}`}
                >
                  🗳️ Padrón {data.padron.anio}
                  {data.padron.escuela ? ` · ${data.padron.escuela}` : ''}
                  {data.padron.mesa ? ` · Mesa ${data.padron.mesa}` : ''}
                  {data.padron.orden ? ` · Orden ${data.padron.orden}` : ''}
                </span>
              )}
              {data?.emprendedor && (
                <span className="bg-amber-400 text-amber-950 px-3 py-1 rounded-full text-xs font-black border border-amber-300 flex items-center gap-1 shadow-sm">
                  🛍️ Emprendedor: {data.emprendedor.rubro}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <button
              onClick={(e) => {
                const p = data?.persona || {}
                const hasTelefono = p.telefono && p.telefono.trim().length > 0 && p.telefono.toLowerCase() !== 'no registrado'
                const hasBarrio = p.barrio && p.barrio.trim().length > 0 && p.barrio.toLowerCase() !== 'sin barrio'
                if (!hasTelefono || !hasBarrio) {
                  e.preventDefault()
                  setShowIncompleteModal(true)
                } else {
                  navigate(`/asistencia/registrar?persona=${p.id}`)
                }
              }}
              className="bg-white text-emerald-800 hover:bg-emerald-50 px-6 py-2 rounded-xl font-bold shadow-lg transition-all flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Nueva Asistencia
            </button>
          </div>
        </div>
          {(persona.es_personal_municipal || fbMeta) && (
            <div className={`mt-6 grid grid-cols-1 gap-3 ${
              persona.es_personal_municipal && fbMeta ? 'lg:grid-cols-2' : ''
            }`}>
              {persona.es_personal_municipal && (
                <div className="rounded-2xl border border-white/15 bg-slate-950/20 p-4 backdrop-blur-sm">
                  <div className="text-[11px] font-black uppercase tracking-[0.18em] text-rose-200">
                    Nota operativa
                  </div>
                  <div className="mt-1 text-base font-bold text-white">
                    Personal Municipal
                  </div>
                  <p className="mt-2 text-sm text-emerald-50/85">
                    Este ciudadano figura marcado como personal municipal. Revise el contexto antes de registrar o auditar una asistencia.
                  </p>
                </div>
              )}

              {fbMeta && (
                <div className="rounded-2xl border border-white/15 bg-slate-950/20 p-4 backdrop-blur-sm">
                  <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className={`inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-black uppercase tracking-[0.16em] ${fbMeta.badgeClass}`}>
                        {fbMeta.title}
                      </div>
                      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs font-bold">
                        <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1 text-white">
                          DNI: {persona.dni}
                        </span>
                        <span className={`rounded-full border border-white/15 bg-white/10 px-3 py-1 ${fbMeta.accentClass}`}>
                          Score: {fbMeta.score ?? '—'}
                        </span>
                        {fbMeta.isCorrected ? (
                          <span className="rounded-full border border-emerald-300 bg-emerald-500/25 px-3 py-1 text-emerald-100 font-bold">
                            ✓ Perfil confirmado
                          </span>
                        ) : fbMatch?.fb_nombre ? (
                          <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1 text-white/90">
                            Perfil sugerido: {fbMatch.fb_nombre}
                          </span>
                        ) : null}
                      </div>
                      <p className="mt-3 text-sm text-white/85">
                        {fbMeta.shortNote}
                      </p>
                    </div>

                    <div className="flex shrink-0 flex-wrap items-center gap-2">
                      {effectiveFbHref ? (
                        <a
                          href={effectiveFbHref}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2 text-sm font-bold text-emerald-800 shadow-lg transition-all hover:bg-emerald-50"
                        >
                          Abrir perfil Facebook
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 3h7v7m0-7L10 14m-4 7h7a2 2 0 002-2v-7" />
                          </svg>
                        </a>
                      ) : (
                        <div className="text-sm font-semibold text-white/70 mr-2">Sin link de Facebook</div>
                      )}

                      <button
                        onClick={openEditModal}
                        type="button"
                        className="inline-flex items-center gap-2 rounded-xl bg-emerald-500/20 border border-white/10 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-500/35 transition-all"
                      >
                        Corregir
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>

                      <button
                        onClick={() => handleReportIncorrectProfile()}
                        type="button"
                        className="inline-flex items-center gap-2 rounded-xl bg-rose-600/30 border border-white/10 px-4 py-2 text-sm font-bold text-rose-100 hover:bg-rose-600/45 transition-all"
                      >
                        Reportar error
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

      {/* Tabs Navigation — cada pestaña con su color identitario, siempre visible */}
      <div className="flex flex-wrap gap-2 rounded-2xl border border-hairline bg-white p-1.5 shadow-card">
        {[
          { id: 'resumen', label: 'Resumen', active: 'bg-hospital-600 text-white shadow-sm', idle: 'text-hospital-700 hover:bg-hospital-50', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
          { id: 'historial', label: `Historial (${asistencias.length})`, active: 'bg-amber-500 text-white shadow-sm', idle: 'text-amber-700 hover:bg-amber-50', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
          { id: 'documentos', label: 'Documentos', active: 'bg-violet-600 text-white shadow-sm', idle: 'text-violet-700 hover:bg-violet-50', icon: 'M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2' },
          { id: 'familia', label: 'Grupo Familiar', active: 'bg-social-700 text-white shadow-sm', idle: 'text-social-700 hover:bg-social-50', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
          { id: 'inundaciones', label: `Inundaciones (${evacuaciones.length})`, active: 'bg-emerald-600 text-white shadow-sm', idle: 'text-emerald-700 hover:bg-emerald-50', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' },
          ...(isAdmin ? [{ id: 'transito', label: `Tránsito (${data?.licencias?.length || 0})`, active: 'bg-emerald-600 text-white shadow-sm', idle: 'text-emerald-700 hover:bg-emerald-50', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' }] : []),
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            aria-current={activeTab === t.id ? 'true' : undefined}
            className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-bold transition-all ${activeTab === t.id ? t.active : t.idle}`}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={t.icon} /></svg>
            {t.label}
          </button>
        ))}
      </div>



      {/* Tab Panels */}
      <div className="min-h-[400px]">
        {activeTab === 'resumen' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="card p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">Datos Personales</h3>
                <button onClick={openEditModal} className="text-emerald-600 hover:text-emerald-800 text-xs font-bold px-3 py-1 bg-emerald-50 rounded-lg transition-colors">Editar</button>
              </div>
              <div className="space-y-4">
                <InfoRow label="DNI" value={persona.dni} isMono />
                <InfoRow
                  label="Fecha Nac."
                  value={formatDate(persona.fecha_nacimiento, persona.nacimiento_solo_anio)}
                />
                <InfoRow label="Sexo" value={formatSexo(persona.sexo || persona.genero)} />
                <InfoRow label="Teléfono Principal" value={persona.telefono || 'No registrado'} />
                <InfoRow label="Otros Teléfonos" value={persona.telefonos_adicionales || 'No registrado'} />
                <InfoRow label="Correo Electrónico" value={persona.email || 'No registrado'} />
              </div>
            </div>
            <div className="card p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">Residencia y Redes</h3>
                <button onClick={openEditModal} className="text-emerald-600 hover:text-emerald-800 text-xs font-bold px-3 py-1 bg-emerald-50 rounded-lg transition-colors">Editar</button>
              </div>
              <div className="space-y-4">
                <InfoRow label="Barrio" value={persona.barrio || 'Sin barrio'} />
                <InfoRow label="Dirección" value={persona.direccion || 'Sin dirección'} />
                <InfoRow label="Lat/Long" value={`${persona.latitud || '-'} / ${persona.longitud || '-'}`} />
                <InfoRow label="Facebook" value={renderSocialLink('facebook', persona.redes_sociales?.facebook || fbMatch?.fb_perfil_override || fbMatch?.fb_perfil)} />
                <InfoRow label="Instagram" value={renderSocialLink('instagram', persona.redes_sociales?.instagram)} />
              </div>
            </div>
            {contexto && (
              <div className="card p-6 md:col-span-2 border-orange-200 bg-orange-50/60">
                <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                  <div>
                    <h3 className="text-sm font-bold text-orange-900 uppercase tracking-wider">Contexto 360</h3>
                    <p className="text-xs text-ink-soft mt-1">Todo lo que el sistema sabe de esta persona. Tocá un indicador para ver el detalle.</p>
                  </div>
                  {contexto.hogar?.numero && (
                    <span className="rounded-full bg-violet-100 px-3 py-1 text-xs font-black text-violet-800">
                      Hogar GF-{String(contexto.hogar.numero).padStart(6, '0')}
                    </span>
                  )}
                </div>
                {(() => {
                  // Mismos datos que las pestañas: el número de "Beneficios" es el del Historial.
                  const beneficios = data?.asistencias || []
                  const monto = beneficios.reduce((a, x) => a + (Number(x.monto) || 0), 0)
                  const ultima = beneficios.map((x) => String(x.fecha || '').slice(0, 10)).filter(Boolean).sort().pop()
                  const vigentes = (contexto.programas || []).filter((p) => p.vigente)
                  const sen = contexto.senales || {}
                  const miembros = contexto.hogar?.miembros?.length || 0
                  const kpis = [
                    { t: 'Beneficios', v: beneficios.length, sub: beneficios.length ? `última ${ultima ? ultima.split('-').reverse().join('/') : '—'}${monto ? ` · $ ${Math.round(monto).toLocaleString('es-AR')}` : ''}` : 'sin entregas registradas', tab: 'historial', color: 'text-amber-700' },
                    { t: 'Hogar', v: miembros || '—', sub: miembros ? `integrantes · ${sen.vinculos || 0} vínculos` : `sin hogar${sen.vinculos ? ` · ${sen.vinculos} vínculos` : ''} · armarlo`, tab: 'familia', color: 'text-violet-700' },
                    { t: 'Programas vigentes', v: vigentes.length, sub: vigentes.length ? vigentes.map(nombrePrograma).join(', ') : 'no está en ningún padrón', tab: null, color: 'text-emerald-700' },
                    { t: 'Documentación', v: sen.dni_fotos === 2 ? 'DNI ✓' : sen.dni_fotos === 1 ? 'DNI ½' : 'Sin DNI', sub: sen.dni_fotos === 2 ? 'frente y dorso cargados' : 'cargar fotos del DNI', tab: 'documentos', color: sen.dni_fotos === 2 ? 'text-emerald-700' : 'text-rose-700' },
                  ]
                  return (
                    <>
                      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                        {kpis.map((k) => (
                          <button key={k.t} type="button" disabled={!k.tab} onClick={() => k.tab && setActiveTab(k.tab)}
                            title={k.tab ? 'Ver el detalle' : undefined}
                            className="rounded-xl border border-orange-100 bg-white p-3 text-left transition-all enabled:hover:-translate-y-0.5 enabled:hover:border-orange-300 enabled:hover:shadow-sm disabled:cursor-default">
                            <p className="text-[10px] font-black uppercase tracking-wide text-ink-muted">{k.t}</p>
                            <p className={`mt-1 text-2xl font-black tabular-nums ${k.color}`}>{k.v}</p>
                            <p className="mt-0.5 line-clamp-2 text-[11px] text-ink-soft">{k.sub}</p>
                          </button>
                        ))}
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2 text-[11px] font-bold">
                        <span className={`rounded-full px-2.5 py-1 ${sen.padron ? 'bg-sky-100 text-sky-800' : 'bg-slate-100 text-ink-muted'}`}>
                          🗳️ {sen.padron
                            ? `Padrón ${sen.padron.anio}${sen.padron.escuela ? ` · ${sen.padron.escuela}` : ''} · mesa ${sen.padron.mesa}${sen.padron.orden ? ` · orden ${sen.padron.orden}` : ''}`
                            : 'No figura en el padrón de San Roque'}
                        </span>
                        {sen.elnino_riesgo && <span className="rounded-full bg-amber-100 px-2.5 py-1 text-amber-800">🌧️ El Niño · riesgo {sen.elnino_riesgo}</span>}
                        {(sen.capacitaciones || []).length > 0 && (
                          <span className="rounded-full bg-social-100 px-2.5 py-1 text-social-900" title={sen.capacitaciones.map((c) => c.nombre).join(' · ')}>
                            🎓 {sen.capacitaciones.length} capacitación{sen.capacitaciones.length === 1 ? '' : 'es'}
                          </span>
                        )}
                        {(sen.produccion || []).length > 0 && (
                          <span className="rounded-full bg-lime-100 px-2.5 py-1 text-lime-900" title={sen.produccion.map((e) => `${e.tipo}${e.producto ? ` · ${e.producto}` : ''}`).join(' | ')}>
                            🌱 {sen.produccion.length} entrega{sen.produccion.length === 1 ? '' : 's'} de Producción
                          </span>
                        )}
                        {/* Retira a nombre de una institución: es una firma, no un beneficio suyo. */}
                        {(sen.retira_para || []).length > 0 && (
                          <span className="rounded-full bg-indigo-100 px-2.5 py-1 text-indigo-800" title={sen.retira_para.join(' · ')}>
                            🏫 Retira para {sen.retira_para.length === 1 ? sen.retira_para[0] : `${sen.retira_para.length} instituciones`}
                          </span>
                        )}
                        {sen.evacuaciones > 0 && (
                          <button type="button" onClick={() => setActiveTab('inundaciones')} className="rounded-full bg-sky-100 px-2.5 py-1 text-sky-800 hover:bg-sky-200">
                            🌊 Evacuada/o {sen.evacuaciones} {sen.evacuaciones === 1 ? 'vez' : 'veces'}
                          </button>
                        )}
                      </div>
                    </>
                  )
                })()}
                {contexto.programas?.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2" aria-label="Programas de la persona">
                    {contexto.programas.map((programa) => (
                      <span key={`${programa.tipo}-${programa.id}`} className={`rounded-full border px-3 py-1 text-xs font-bold ${programa.vigente ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-hairline bg-slate-50 text-ink-muted'}`}>
                        {nombrePrograma(programa)}{programa.vigente ? '' : ' · inactivo'}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
            {contexto?.familia && (
              <FamiliaYAyuda
                familia={contexto.familia}
                puedeCargarTutor={hasSection(user, 'comedores')}
                onCambio={recargarContexto}
                onIrAGrupoFamiliar={() => setActiveTab('familia')}
                className={`md:col-span-2 ${esChico ? 'order-first' : ''}`}
              />
            )}
          </div>
        )}

        {activeTab === 'historial' && (
          <div className="card divide-y divide-gray-100 overflow-hidden">
            {asistencias.length === 0 ? (
              <div className="p-12 text-center text-gray-400">
                No hay asistencias registradas
              </div>
            ) : (
              asistencias.map((asistencia) => (
                <div key={asistencia.id} className="p-5 hover:bg-gray-50/50 transition-colors flex items-start gap-4 animate-in fade-in duration-300">
                      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm border flex-shrink-0 ${
                        asistencia.is_evacuacion
                          ? 'bg-blue-50 text-blue-500 border-blue-100/50'
                          : asistencia.is_capacitacion
                            ? 'bg-social-50 text-social-700 border-social-200'
                          : asistencia.is_produccion
                            ? 'bg-lime-50 text-lime-700 border-lime-200'
                          : asistencia.is_reclamo
                            ? 'bg-orange-50 text-orange-600 border-orange-200'
                          : asistencia.is_vale 
                            ? 'bg-amber-50 text-amber-500 border-amber-100/50' 
                            : asistencia.is_mesa_entrada
                              ? 'bg-indigo-50 text-indigo-500 border-indigo-100/50'
                              : (asistencia.is_emprendedor || asistencia.is_emprendedor_ayuda)
                                ? 'bg-amber-50 text-amber-600 border-amber-200/60'
                                : asistencia.is_viaje
                                  ? 'bg-sky-50 text-sky-600 border-sky-200/60'
                                  : asistencia.is_whatsapp
                                    ? 'bg-emerald-50 text-emerald-600 border-emerald-200/60'
                                    : 'bg-emerald-50 text-emerald-500 border-emerald-100/50'
                      }`}>
                        {asistencia.is_evacuacion
                          ? '🌊'
                          : asistencia.is_capacitacion
                            ? '🎓'
                          : asistencia.is_produccion
                            ? '🌱'
                          : asistencia.is_reclamo
                            ? '📣'
                          : asistencia.is_mesa_entrada
                            ? '📥'
                            : asistencia.is_emprendedor
                              ? '🛍️'
                              : asistencia.is_emprendedor_ayuda
                                ? '🎁'
                                : asistencia.is_viaje
                                  ? (asistencia.tipo.includes('Colectivo') ? '🚌' : '🚐')
                                  : asistencia.is_whatsapp
                                    ? '💬'
                                    : getTipoIcon(asistencia.tipo)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h4 className="font-bold text-gray-900 text-lg group-hover:text-emerald-700 transition-colors">
                              {formatTipo(asistencia.tipo)}
                            </h4>
                            <p className="text-gray-500 text-sm font-medium mt-0.5 line-clamp-2 tracking-tight">
                              {asistencia.detalle}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-2 flex-shrink-0">
                            {asistencia.is_evacuacion ? (
                              <span className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50/50 px-2.5 py-1 text-[8px] font-black uppercase tracking-[0.12em] text-blue-700 shadow-xs">
                                Registro de Evacuación
                              </span>
                            ) : asistencia.is_reclamo ? (
                              <span className="inline-flex items-center gap-1 rounded-full border border-orange-300 bg-orange-100 px-2.5 py-1 text-[8px] font-black uppercase tracking-[0.12em] text-orange-900 shadow-xs">
                                Reclamo
                              </span>
                            ) : asistencia.is_produccion ? (
                              <span className="inline-flex items-center gap-1 rounded-full border border-lime-300 bg-lime-100 px-2.5 py-1 text-[8px] font-black uppercase tracking-[0.12em] text-lime-900 shadow-xs">
                                Producción
                              </span>
                            ) : asistencia.is_capacitacion ? (
                              <span className="inline-flex items-center gap-1 rounded-full border border-social-300 bg-social-100 px-2.5 py-1 text-[8px] font-black uppercase tracking-[0.12em] text-social-900 shadow-xs">
                                Capacitación
                              </span>
                            ) : asistencia.is_vale ? (
                              <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50/50 px-2.5 py-1 text-[8px] font-black uppercase tracking-[0.12em] text-amber-700 shadow-xs">
                                Vale de Retiro
                              </span>
                            ) : asistencia.is_mesa_entrada ? (
                              <span className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50/50 px-2.5 py-1 text-[8px] font-black uppercase tracking-[0.12em] text-indigo-700 shadow-xs">
                                Mesa de Entrada
                              </span>
                            ) : asistencia.is_emprendedor ? (
                              <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-100 px-2.5 py-1 text-[8px] font-black uppercase tracking-[0.12em] text-amber-900 shadow-xs">
                                Padrón Emprendedores
                              </span>
                            ) : asistencia.is_emprendedor_ayuda ? (
                              <span className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-100 px-2.5 py-1 text-[8px] font-black uppercase tracking-[0.12em] text-emerald-900 shadow-xs">
                                Ayuda Emprendedor
                              </span>
                            ) : asistencia.is_viaje ? (
                              <span className="inline-flex items-center gap-1 rounded-full border border-sky-300 bg-sky-100 px-2.5 py-1 text-[8px] font-black uppercase tracking-[0.12em] text-sky-900 shadow-xs">
                                {asistencia.vehiculo_nombre || 'Viaje Municipal'}
                              </span>
                            ) : asistencia.is_whatsapp ? (
                              <span className="inline-flex items-center gap-1 rounded-full border border-emerald-400 bg-emerald-100 px-2.5 py-1 text-[8px] font-black uppercase tracking-[0.12em] text-emerald-950 shadow-xs">
                                Mensaje WhatsApp
                              </span>
                            ) : (
                              <button
                                type="button"
                                onClick={() => handleEditAsistenciaOpen(asistencia)}
                                className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-1 text-[9px] font-black uppercase tracking-[0.16em] text-amber-700 transition-colors hover:bg-amber-100"
                              >
                                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                Editar
                              </button>
                            )}
                          </div>
                        </div>
                    <div className="flex items-center gap-4 mt-3">
                      {/* La fecha es el dato que mas se busca al revisar el
                          historial: iba en 10px y gris claro (text-gray-400),
                          casi ilegible y sin contraste suficiente. Va en text-sm
                          y en el negro del sistema (ink = #262626). */}
                      <span className="text-sm font-black text-ink flex items-center gap-1.5">
                        <svg className="w-4 h-4 text-ink-soft" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {formatDate(asistencia.fecha)}
                      </span>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-gray-100 text-gray-500 uppercase">
                        {asistencia.organismo.replace(/_/g, ' ')}
                      </span>
                      {asistencia.nro_expediente && (
                        <span className="text-[10px] font-black px-2.5 py-1 rounded-lg bg-amber-50 text-amber-700 border border-amber-200 shadow-sm flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                          EXP: {asistencia.nro_expediente}
                        </span>
                      )}
                      {asistencia.is_viaje ? (
                        <span className={`text-[10px] font-black px-2.5 py-1 rounded-lg border shadow-xs ${
                          asistencia.estado === 'subio' || /SUBIÓ \(PRESENTE\)/i.test(asistencia.detalle || '')
                            ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                            : asistencia.estado === 'no_subio' || /NO SUBIÓ \(AUSENTE\)/i.test(asistencia.detalle || '')
                              ? 'bg-rose-100 text-rose-800 border-rose-300'
                              : 'bg-amber-50 text-amber-800 border-amber-200'
                        }`}>
                          {asistencia.estado === 'subio' || /SUBIÓ \(PRESENTE\)/i.test(asistencia.detalle || '')
                            ? '✅ Subió (Presente)'
                            : asistencia.estado === 'no_subio' || /NO SUBIÓ \(AUSENTE\)/i.test(asistencia.detalle || '')
                              ? '❌ No subió (Ausente)'
                              : '⏳ Reservado / Pendiente'}
                        </span>
                      ) : !asistencia.is_evacuacion && Number(asistencia.monto || 0) > 0 ? (
                        <span className="text-[10px] font-black px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 shadow-sm">
                          ${Number(asistencia.monto || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                        </span>
                      ) : null}
                      {Number(asistencia.cantidad || 0) > 0 && (
                        <span className="text-[10px] font-black px-2.5 py-1 rounded-lg bg-blue-50 text-blue-700 border border-blue-200 shadow-sm">
                          CANT: {asistencia.cantidad}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {isAdmin && activeTab === 'transito' && (
          <div className="space-y-6 animate-in fade-in duration-200">
            {(() => {
              const licencias = data?.licencias || [];
              const latestLic = licencias[0]; // ordenado por fecha DESC en el backend
              
              if (!latestLic) {
                return (
                  <div className="card p-12 text-center text-slate-400 bg-white border border-slate-100 rounded-3xl flex flex-col items-center justify-center">
                    <svg className="w-16 h-16 mb-4 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <div className="text-lg font-black text-slate-700">Sin Trámites de Tránsito</div>
                    <p className="text-slate-400 text-sm mt-1 max-w-sm">No se registran trámites de licencias de conducir ni cobros en esta oficina para este ciudadano.</p>
                  </div>
                );
              }

              // Calcular vigencia
              let isVigente = false;
              let diffDays = null;
              if (latestLic.fecha_vencimiento) {
                const venc = new Date(latestLic.fecha_vencimiento);
                const hoy = new Date();
                hoy.setHours(0,0,0,0);
                venc.setHours(0,0,0,0);
                const diffTime = venc.getTime() - hoy.getTime();
                diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                isVigente = diffDays >= 0;
              }

              return (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Tarjeta de Estado Licencia */}
                    <div className={`card p-6 border-l-4 rounded-3xl bg-white shadow-sm border border-slate-100 ${isVigente ? 'border-l-emerald-500' : 'border-l-rose-500'}`}>
                      <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Estado de Licencia</h3>
                      <div className="flex items-center gap-3">
                        <span className={`text-2xl font-black ${isVigente ? 'text-emerald-700' : 'text-rose-700'}`}>
                          {isVigente ? 'Vigente' : 'Vencida'}
                        </span>
                        {isVigente && (
                          <span className="bg-emerald-100 text-emerald-800 text-[10px] px-2.5 py-1 rounded-full font-black uppercase tracking-wider">
                            Activo
                          </span>
                        )}
                      </div>
                      {latestLic.fecha_vencimiento ? (
                        <p className="text-xs font-bold text-slate-500 mt-3 flex items-center gap-1">
                          <svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {isVigente 
                            ? `Vence en ${diffDays} días (${new Date(latestLic.fecha_vencimiento).toLocaleDateString('es-AR')})` 
                            : `Venció hace ${Math.abs(diffDays)} días (${new Date(latestLic.fecha_vencimiento).toLocaleDateString('es-AR')})`}
                        </p>
                      ) : (
                        <p className="text-xs font-bold text-slate-400 mt-3">Sin fecha de vencimiento</p>
                      )}
                    </div>

                    {/* Tarjeta de Último Trámite */}
                    <div className="card p-6 bg-white border-l-4 border-l-blue-500 rounded-3xl shadow-sm border border-slate-100">
                      <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Último Trámite</h3>
                      <span className="text-xl font-black text-slate-800 line-clamp-1">
                        {latestLic.tramite}
                      </span>
                      <p className="text-xs font-bold text-slate-500 mt-3 flex items-center gap-1">
                        <svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Pago: {new Date(latestLic.fecha_pago).toLocaleDateString('es-AR')}
                      </p>
                    </div>

                    {/* Tarjeta de Historial Financiero */}
                    <div className="card p-6 bg-white border-l-4 border-l-amber-500 rounded-3xl shadow-sm border border-slate-100">
                      <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Total Contribuido</h3>
                      <span className="text-2xl font-black text-slate-800">
                        ${licencias.reduce((sum, l) => sum + Number(l.monto || 0), 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                      </span>
                      <p className="text-xs font-bold text-slate-500 mt-3 flex items-center gap-1">
                        <svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        Trámites totales: {licencias.length}
                      </p>
                    </div>
                  </div>

                  {/* Tabla de Historial de Licencias */}
                  <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
                    <div className="px-6 py-5 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
                      <h3 className="font-black text-slate-900 text-lg">Historial de Trámites y Pagos</h3>
                      <span className="text-[10px] font-black text-slate-500 px-3 py-1 bg-slate-100 rounded-lg uppercase tracking-wider">Tránsito Municipal</span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="bg-slate-50 text-slate-500 text-[10px] font-black uppercase tracking-wider border-b border-slate-100">
                            <th className="px-6 py-4">Fecha Pago</th>
                            <th className="px-6 py-4">Trámite</th>
                            <th className="px-6 py-4">Forma de Pago</th>
                            <th className="px-6 py-4">Nro. Operación</th>
                            <th className="px-6 py-4 text-right">Monto</th>
                            <th className="px-6 py-4 text-center">Vencimiento</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 text-slate-600 text-xs font-semibold">
                          {licencias.map((l) => (
                            <tr key={l.id} className="hover:bg-slate-50/20 transition-colors">
                              <td className="px-6 py-4 whitespace-nowrap text-slate-500 font-mono">
                                {new Date(l.fecha_pago).toLocaleDateString('es-AR')}
                              </td>
                              <td className="px-6 py-4">
                                <div className="font-bold text-slate-900 text-sm">{l.tramite}</div>
                                {l.observaciones && <div className="text-[10px] text-slate-400 font-medium mt-0.5">{l.observaciones}</div>}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-slate-100 text-slate-600 uppercase">
                                  {l.forma_pago || 'Efectivo'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap font-mono">
                                {l.nro_operacion || '-'}
                              </td>
                              <td className="px-6 py-4 text-right font-black text-slate-800 text-sm whitespace-nowrap">
                                ${Number(l.monto || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                              </td>
                              <td className="px-6 py-4 text-center whitespace-nowrap">
                                {l.fecha_vencimiento ? (
                                  <span className={`text-[10px] font-black px-2.5 py-1 rounded-lg ${new Date(l.fecha_vencimiento) >= new Date() ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-rose-50 text-rose-700 border border-rose-100'}`}>
                                    {new Date(l.fecha_vencimiento).toLocaleDateString('es-AR')}
                                  </span>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* DNI y recetas: donde se recupera todo lo que se adjuntó a esta persona */}
        {activeTab === 'documentos' && <DocumentosPersona personaId={id} />}

        {activeTab === 'familia' && (
          <div className="space-y-6">
            {/* El hogar se recarga cuando cambian los vínculos: agregar un familiar que convive lo suma. */}
            <HogarPersona key={familiares.map((f) => f.id).join()} personaId={id} persona={data?.persona} />
            <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
              {/* Header premium */}
              <div className="bg-gradient-to-r from-social-50 via-white to-emerald-50 px-6 py-5 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-social-500 to-emerald-500 flex items-center justify-center shadow-lg shadow-social-500/20">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <div>
                      <h2 className="text-xl font-black text-slate-900">Vínculos familiares</h2>
                      <p className="text-sm text-slate-500 mt-0.5 font-medium">
                        {familiares.length > 0
                          ? <><span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-black mr-1">{familiares.length}</span> integrante{familiares.length > 1 ? 's' : ''} registrado{familiares.length > 1 ? 's' : ''}</>
                          : 'Parentescos con otras personas (se ven desde las dos fichas)'
                        }
                      </p>
                    </div>
                  </div>
                  {!showFamilyForm && (
                    <button
                      type="button"
                      onClick={() => {
                        setEditingMember(null)
                        setShowFamilyForm(true)
                      }}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 text-sm font-bold text-white shadow-lg shadow-emerald-600/20 hover:from-emerald-500 hover:to-teal-500 transition-all"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Agregar Familiar
                    </button>
                  )}
                </div>
              </div>

              <div className="p-6">
                {showFamilyForm ? (
                  <div className="rounded-2xl border-2 border-dashed border-emerald-200 bg-gradient-to-br from-emerald-50/30 to-teal-50/30 p-6">
                    <div className="flex items-center gap-3 mb-5">
                      <div className="w-8 h-8 rounded-xl bg-emerald-100 flex items-center justify-center">
                        <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-lg font-black text-slate-900">
                          {editingMember ? 'Editar Familiar' : 'Nuevo Familiar'}
                        </h3>
                        <p className="text-xs text-slate-500 font-medium">
                          {editingMember ? 'Modifique los datos del integrante' : 'Busque por DNI para vincular una persona existente o ingrese los datos manualmente'}
                        </p>
                      </div>
                    </div>
                    {isSavingFamily ? (
                      <div className="flex flex-col items-center justify-center p-12 gap-3">
                        <Loading size="lg" />
                        <p className="text-sm font-bold text-slate-400">Guardando familiar...</p>
                      </div>
                    ) : (
                      <FamilyMemberForm
                        initialData={editingMember}
                        onSubmit={editingMember ? handleUpdateFamilyMember : handleAddFamilyMember}
                        onCancel={() => {
                          setShowFamilyForm(false)
                          setEditingMember(null)
                        }}
                      />
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {familiares.length === 0 && relaciones.length === 0 ? (
                      <div className="col-span-full py-16 text-center">
                        <div className="mx-auto mb-4 w-20 h-20 rounded-full bg-slate-50 flex items-center justify-center">
                          <svg className="w-10 h-10 text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-bold text-slate-400 mb-1">No hay familiares registrados</h3>
                        <p className="text-sm text-slate-300 max-w-sm mx-auto">Presione "Agregar Familiar" para vincular integrantes del grupo familiar</p>
                      </div>
                    ) : (
                      <>
                        {familiares.map((familiar) => (
                          <div
                            key={`familiar-${familiar.id}`}
                            className="p-4 bg-white border border-slate-100 rounded-2xl hover:border-emerald-200 hover:shadow-md transition-all group"
                          >
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-500 flex items-center justify-center">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-bold text-gray-900 truncate uppercase">
                                  {familiar.apellido}, {familiar.nombre}
                                </p>
                                <p className="text-xs text-emerald-600 font-black uppercase mt-0.5 tracking-wider">
                                  {String(familiar.relacion || '').replace(/_/g, ' ')}
                                  {familiar.inverso && <span className="ml-2 text-[10px] font-bold normal-case tracking-normal text-slate-400" title="Este vínculo se cargó desde la ficha de la otra persona">· cargado en su ficha</span>}
                                </p>
                                <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-gray-500 font-bold uppercase">
                                  {familiar.dni && <span className="bg-gray-100 px-1.5 py-0.5 rounded">DNI: {familiar.dni}</span>}
                                  {familiar.edad != null && <span className="bg-gray-100 px-1.5 py-0.5 rounded">{familiar.edad} AÑOS</span>}
                                  <span className={`${familiar.convive ? 'text-emerald-600 bg-emerald-50' : 'text-gray-400 bg-gray-50'} px-1.5 py-0.5 rounded`}>
                                    {familiar.convive ? 'CONVIVE' : 'NO CONVIVE'}
                                  </span>
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                {!familiar.inverso && <button
                                  onClick={() => {
                                    setEditingMember(familiar)
                                    setShowFamilyForm(true)
                                  }}
                                  className="p-2 hover:bg-emerald-50 text-emerald-600 rounded-lg transition-colors"
                                  title="Editar"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                  </svg>
                                </button>}
                                <button
                                  onClick={() => handleDeleteFamilyMember(familiar.id)}
                                  className="p-2 hover:bg-red-50 text-red-600 rounded-lg transition-colors"
                                  title="Eliminar"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                                {familiar.persona_relacionada_id && (
                                  <Link
                                    to={`/persona/${familiar.persona_relacionada_id}`}
                                    className="p-2 hover:bg-blue-50 text-blue-600 rounded-lg transition-colors"
                                    title="Ver Perfil"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                  </Link>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}

                        {relaciones.map((relacion) => (
                          <Link
                            key={`relacion-${relacion.id}`}
                            to={`/persona/${String(relacion.destino_persona_id) === String(id) ? relacion.origen_persona_id : relacion.destino_persona_id}`}
                            className="p-4 bg-white border border-gray-100 rounded-2xl hover:border-emerald-200 hover:shadow-md transition-all group"
                          >
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-12 rounded-xl bg-gray-50 text-gray-400 flex items-center justify-center group-hover:bg-emerald-50 group-hover:text-emerald-500 transition-colors">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-bold text-gray-900 group-hover:text-emerald-700 truncate uppercase">
                                  {String(relacion.destino_persona_id) === String(id)
                                    ? `${relacion.origen_apellido}, ${relacion.origen_nombre}`
                                    : `${relacion.destino_apellido}, ${relacion.destino_nombre}`
                                  }
                                </p>
                                <p className="text-xs text-gray-500 font-black uppercase mt-0.5 tracking-wider">
                                  {relacion.tipo_relacion.replace(/_/g, ' ')}
                                </p>
                              </div>
                              <div className="bg-gray-100 group-hover:bg-emerald-100 p-1.5 rounded-lg transition-colors">
                                <svg className="w-4 h-4 text-gray-400 group-hover:text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </div>
                            </div>
                          </Link>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'inundaciones' && (
          <div className="space-y-6 animate-in fade-in duration-300">
            <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
              <div className="bg-gradient-to-r from-emerald-50 via-white to-teal-50 px-6 py-5 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 text-emerald-700 rounded-xl">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">Registro de Evacuaciones por Inundaciones</h3>
                    <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Historial de asistencia ante emergencias climáticas</p>
                  </div>
                </div>
              </div>

              <div className="p-6">
                {evacuaciones.length === 0 ? (
                  <div className="text-center py-12 text-gray-400 font-medium italic">
                    Este ciudadano no figura registrado en ningún evento histórico de inundaciones.
                  </div>
                ) : (
                  <div className="space-y-6">
                    {evacuaciones.map((ev) => (
                      <div key={ev.id} className="relative pl-6 pb-2 border-l border-emerald-200 last:border-0 last:pb-0">
                        {/* Timeline dot */}
                        <span className="absolute -left-[6.5px] top-1.5 flex h-3 w-3 items-center justify-center rounded-full bg-emerald-500 ring-4 ring-emerald-50" />

                        <div className="bg-slate-50 border border-slate-200/60 rounded-2xl p-5 hover:shadow-sm transition-all">
                          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                            <div>
                              <h4 className="font-extrabold text-slate-800 text-sm uppercase">{ev.evento_nombre}</h4>
                              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-0.5">
                                Registrado el {ev.fecha_registro ? new Date(ev.fecha_registro).toLocaleDateString('es-AR') : '-'}
                              </p>
                            </div>
                            <span className="px-3 py-1 bg-emerald-100/75 border border-emerald-200 text-emerald-800 text-[10px] font-black uppercase tracking-wider rounded-full">
                              📍 {ev.centro_evacuacion}
                            </span>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-xs mt-4 border-t border-slate-200/50 pt-4">
                            <div>
                              <span className="font-bold text-slate-400 uppercase text-[10px] block">Zona Afectada</span>
                              <span className="font-bold text-slate-800">{ev.barrio_afectado}</span>
                              {ev.direccion_afectada && <p className="text-slate-500 mt-0.5">{ev.direccion_afectada}</p>}
                            </div>

                            <div>
                              <span className="font-bold text-slate-400 uppercase text-[10px] block">Grupo Familiar</span>
                              <span className="font-extrabold text-slate-800">Total: {ev.cantidad_integrantes} integrantes</span>
                              <p className="text-slate-500 mt-0.5">
                                (Adultos: {ev.cantidad_adultos}, Menores: {ev.cantidad_menores})
                              </p>
                            </div>

                            <div>
                              <span className="font-bold text-slate-400 uppercase text-[10px] block">Pérdidas Materiales</span>
                              <p className="text-slate-700 italic">
                                {ev.pertenencias_perdidas || <span className="text-slate-400">- Sin registrar -</span>}
                              </p>
                            </div>

                            <div>
                              <span className="font-bold text-slate-400 uppercase text-[10px] block">Necesidades</span>
                              <p className="text-indigo-600 italic">
                                {ev.necesidades || <span className="text-slate-400">- Sin registrar -</span>}
                              </p>
                            </div>
                          </div>

                          {ev.observaciones && (
                            <div className="mt-4 pt-3 border-t border-slate-200/40 text-xs text-slate-500 italic">
                              <span className="font-bold text-slate-400 uppercase text-[9.5px] block not-italic mb-0.5">Observaciones adicionales</span>
                              {ev.observaciones}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editModalOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white rounded-3xl w-full max-w-2xl shadow-2xl overflow-hidden my-8">
            <div className="bg-emerald-600 px-6 py-4 flex justify-between items-center text-white">
              <h3 className="font-black text-lg">Modificar Datos</h3>
              <button onClick={() => setEditModalOpen(false)} className="text-emerald-100 hover:text-white transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <form onSubmit={handleUpdateProfile} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Nombre</label>
                  <input required type="text" value={editForm.nombre} onChange={e => setEditForm({...editForm, nombre: e.target.value.toUpperCase()})} className="input uppercase" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Apellido</label>
                  <input required type="text" value={editForm.apellido} onChange={e => setEditForm({...editForm, apellido: e.target.value.toUpperCase()})} className="input uppercase" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">DNI</label>
                  <input required type="text" value={editForm.dni} onChange={e => setEditForm({...editForm, dni: e.target.value})} className="input font-mono" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Sexo</label>
                  <select value={editForm.sexo} onChange={e => setEditForm({...editForm, sexo: e.target.value})} className="input">
                    <option value="">No especificado</option>
                    <option value="M">Masculino</option>
                    <option value="F">Femenino</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Fecha Nacimiento</label>
                  <input type="date" value={editForm.fecha_nacimiento} onChange={e => setEditForm({...editForm, fecha_nacimiento: e.target.value})} className="input" />
                </div>
                 <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Teléfono Principal</label>
                  <input type="tel" value={editForm.telefono} onChange={e => setEditForm({...editForm, telefono: e.target.value})} className="input" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Teléfonos Adicionales</label>
                  <input type="text" value={editForm.telefonos_adicionales} onChange={e => setEditForm({...editForm, telefonos_adicionales: e.target.value})} className="input" placeholder="Ej: 3777-123456, 3777-654321" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Correo Electrónico</label>
                  <input type="email" value={editForm.email} onChange={e => setEditForm({...editForm, email: e.target.value})} className="input" placeholder="ejemplo@correo.com" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Facebook (Enlace o Usuario)</label>
                  <input type="text" value={editForm.facebook} onChange={e => setEditForm({...editForm, facebook: e.target.value})} className="input" placeholder="https://facebook.com/usuario" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Instagram (Enlace o Usuario)</label>
                  <input type="text" value={editForm.instagram} onChange={e => setEditForm({...editForm, instagram: e.target.value})} className="input" placeholder="https://instagram.com/usuario" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Barrio</label>
                  <input type="text" list="san-roque-barrios-edit" value={editForm.barrio} onChange={e => setEditForm({...editForm, barrio: normalizeBarrioLabel(e.target.value)})} className="input mb-2" placeholder="Seleccione un barrio o escriba otro" />
                  <datalist id="san-roque-barrios-edit">
                    {BARRIO_OPTIONS.map(b => <option key={b} value={b} />)}
                  </datalist>
                  <div className="flex flex-wrap gap-2">
                    {SAN_ROQUE_BARRIOS.map(barrio => (
                      <button key={barrio} type="button" onClick={() => setEditForm({...editForm, barrio})} className={`rounded-full border px-2 py-1 text-[10px] font-bold transition-all ${editForm.barrio === barrio ? 'border-emerald-600 bg-emerald-600 text-white' : 'border-emerald-100 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`}>{barrio}</button>
                    ))}
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Dirección Exacta</label>
                  <input type="text" value={editForm.direccion} onChange={e => setEditForm({...editForm, direccion: e.target.value})} className="input" />
                </div>
                <div className="md:col-span-2 flex items-center gap-3 bg-slate-50 p-4 rounded-2xl border border-slate-100">
                  <input
                    type="checkbox"
                    id="edit_es_personal_municipal"
                    checked={editForm.es_personal_municipal}
                    onChange={e => setEditForm({...editForm, es_personal_municipal: e.target.checked})}
                    className="w-5 h-5 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500"
                  />
                  <label htmlFor="edit_es_personal_municipal" className="text-sm font-bold text-slate-700 cursor-pointer select-none">
                    ¿Es Personal Municipal?
                  </label>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Observaciones de Personal / Nota de Empleado</label>
                  <textarea
                    rows={2}
                    value={editForm.observaciones_personal}
                    onChange={e => setEditForm({...editForm, observaciones_personal: e.target.value})}
                    className="input py-2"
                    placeholder="Detalles del cargo municipal o notas internas..."
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button type="button" onClick={() => setEditModalOpen(false)} className="px-5 py-2.5 rounded-xl font-bold text-gray-600 hover:bg-gray-100 transition-colors">Cancelar</button>
                <button type="submit" disabled={updating} className="px-5 py-2.5 rounded-xl font-bold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-50">
                  {updating ? 'Guardando...' : 'Guardar Cambios'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Incomplete Data Modal */}
      {showIncompleteModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden my-8 animate-in zoom-in-95">
            <div className="bg-amber-500 px-6 py-4 flex justify-between items-center text-white">
              <h3 className="font-black text-lg flex items-center gap-2">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                Datos Incompletos
              </h3>
              <button onClick={() => setShowIncompleteModal(false)} className="text-amber-100 hover:text-white transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="p-6">
              <p className="text-gray-700 font-medium mb-4">
                El ciudadano tiene datos importantes incompletos (como el <span className="font-bold">Teléfono</span> o el <span className="font-bold">Barrio</span>).
              </p>
              <p className="text-gray-600 text-sm mb-6">
                Para mantener la calidad de la información, por favor verifique con el DNI o consulte al ciudadano para completar estos datos antes de registrar una nueva asistencia.
              </p>
              <div className="flex flex-col gap-3">
                <button 
                  onClick={() => {
                    setShowIncompleteModal(false)
                    openEditModal()
                  }} 
                  className="w-full bg-amber-500 hover:bg-amber-600 text-white font-bold py-3 px-4 rounded-xl transition-colors"
                >
                  Completar Datos Ahora
                </button>
                <button 
                  onClick={() => {
                    setShowIncompleteModal(false)
                    navigate(`/asistencia/registrar?persona=${data?.persona?.id}`)
                  }} 
                  className="w-full bg-white text-gray-500 hover:bg-gray-50 border border-gray-200 font-bold py-3 px-4 rounded-xl transition-colors"
                >
                  Continuar de todos modos
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edición de Asistencia */}
      <Modal
        isOpen={Boolean(editingAsistencia)}
        onClose={() => !savingAsistencia && setEditingAsistencia(null)}
        title="Editar Asistencia"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Motivo / Gestión</label>
            <AssistanceTypeCombobox
              value={editAsistenciaForm.tipo}
              onChange={(tipo) => setEditAsistenciaForm(prev => ({ ...prev, tipo }))}
              categories={assistanceTypes.categories}
              loading={typesLoading}
              placeholder="Buscar tipo..."
            />
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Fecha</label>
            <input
              type="date"
              value={editAsistenciaForm.fecha}
              onChange={(e) => setEditAsistenciaForm(prev => ({ ...prev, fecha: e.target.value }))}
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
            />
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Detalle / Observación</label>
            <textarea
              value={editAsistenciaForm.detalle}
              onChange={(e) => setEditAsistenciaForm(prev => ({ ...prev, detalle: e.target.value }))}
              rows={3}
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all resize-none"
              placeholder="Detalle de la asistencia..."
            />
          </div>

          <div className="rounded-2xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-700">
            <div className="flex items-center gap-2">
              <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Solo puede editar asistencias que usted registró. Los cambios quedan registrados en auditoría.
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => setEditingAsistencia(null)}
              disabled={savingAsistencia}
              className="rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-bold text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleEditAsistenciaSave}
              disabled={savingAsistencia}
              className="rounded-xl bg-gradient-to-r from-teal-600 to-emerald-600 px-5 py-2.5 text-sm font-bold text-white transition-all hover:from-teal-500 hover:to-emerald-500 disabled:opacity-50 shadow-lg shadow-teal-600/20"
            >
              {savingAsistencia ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

const renderSocialLink = (platform, username) => {
  if (!username) return 'No registrado'
  let url = username
  if (!username.startsWith('http://') && !username.startsWith('https://')) {
    if (platform === 'facebook') {
      url = `https://facebook.com/${username}`
    } else if (platform === 'instagram') {
      url = `https://instagram.com/${username}`
    }
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" className="text-emerald-600 hover:text-emerald-700 hover:underline inline-flex items-center gap-1 font-bold">
      {username}
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
      </svg>
    </a>
  )
}

const InfoRow = ({ label, value, isMono }) => (
  <div className="flex justify-between items-center py-2 border-b border-gray-50 last:border-0">
    <span className="text-xs font-bold text-gray-400 uppercase">{label}</span>
    <span className={`text-sm font-semibold text-gray-700 ${isMono ? 'font-mono' : ''}`}>{value}</span>
  </div>
)

export default Profile
